import React, {useContext, useState, useEffect, useRef} from 'react';
import {Button, TextField, Typography, Divider} from '@mui/material';
import {ApplicationContext} from '../context/ApplicationContext';
import {submitLogin} from '../services/login.service';
import {CircularProgress} from "@mui/material";
import config from "../config";
import './Login.scss';

const demoUsername = 'jsmith';
const demoPassword = '12345678';

const PARTICLE_COLORS = [
    'rgba(99, 102, 241,',
    'rgba(139, 92, 246,',
    'rgba(167, 139, 250,',
    'rgba(79, 70, 229,',
];

const LoginPage = () => {
    const AppContext = useContext(ApplicationContext);
    const [providedUsername, setUsername] = useState('');
    const [providedPassword, setPassword] = useState('');
    const [isWaitingOnServer, setIsWaitingOnServer] = useState(false);
    const canvasRef = useRef(null);

    useEffect(() => {
        const canvas = canvasRef.current;
        if (!canvas) return;
        const ctx = canvas.getContext('2d');

        const resize = () => {
            canvas.width = window.innerWidth;
            canvas.height = window.innerHeight;
        };
        resize();
        window.addEventListener('resize', resize);

        const particles = Array.from({length: 70}, () => ({
            x: Math.random() * window.innerWidth,
            y: Math.random() * window.innerHeight,
            r: Math.random() * 2.5 + 0.5,
            speed: Math.random() * 0.6 + 0.25,
            opacity: Math.random() * 0.6 + 0.2,
            opacityDir: Math.random() > 0.5 ? 1 : -1,
            opacitySpeed: Math.random() * 0.006 + 0.002,
            color: PARTICLE_COLORS[Math.floor(Math.random() * PARTICLE_COLORS.length)],
            drift: (Math.random() - 0.5) * 0.4,
        }));

        let animId;
        const draw = () => {
            ctx.clearRect(0, 0, canvas.width, canvas.height);
            particles.forEach(p => {
                p.y -= p.speed;
                p.x += p.drift;
                p.opacity += p.opacityDir * p.opacitySpeed;
                if (p.opacity > 0.9 || p.opacity < 0.1) p.opacityDir *= -1;
                if (p.y < -10) {
                    p.y = canvas.height + 10;
                    p.x = Math.random() * canvas.width;
                }

                ctx.beginPath();
                ctx.arc(p.x, p.y, p.r, 0, Math.PI * 2);
                ctx.fillStyle = `${p.color}${p.opacity})`;
                ctx.shadowBlur = 8;
                ctx.shadowColor = `${p.color}0.9)`;
                ctx.fill();
                ctx.shadowBlur = 0;
            });
            animId = requestAnimationFrame(draw);
        };
        draw();

        return () => {
            cancelAnimationFrame(animId);
            window.removeEventListener('resize', resize);
        };
    }, []);

    const submitForm = (username, password) => {
        setIsWaitingOnServer(true);
        submitLogin(username, password)
            .then((reply) => {
                if (reply && reply.requires_setup) {
                    const destination_page = `${config.uiBaseUrl}/setup2fa?username=${encodeURIComponent(reply.username)}`;
                    window.location.replace(destination_page);
                } else if (reply && reply.requires_2fa) {
                    const destination_page = `${config.uiBaseUrl}/verify2fa?username=${encodeURIComponent(reply.username)}`;
                    window.location.replace(destination_page);
                }
                setIsWaitingOnServer(false);
            })
            .catch((err) => {
                console.log(err);
                AppContext.handleError('Failed login.');
                setIsWaitingOnServer(false);
            });
    };

    const handleKeyDown = (e) => {
        if (e.key === 'Enter' && providedUsername && providedPassword && !isWaitingOnServer) {
            submitForm(providedUsername, providedPassword);
        }
    };

    const background = (
        <>
            <canvas ref={canvasRef} className="login-particles-canvas" aria-hidden="true" />
            <div className="login-bg-orbs" aria-hidden="true">
                <div className="orb orb-1" />
                <div className="orb orb-2" />
                <div className="orb orb-3" />
                <div className="orb orb-4" />
            </div>
        </>
    );

    if (isWaitingOnServer) {
        return (
            <div className="login-container">
                {background}
                <div className="login-card loading-state">
                    <CircularProgress size={48} style={{color: '#6366f1', marginBottom: '24px'}}/>
                    <Typography variant="h6" className="primary-font" style={{fontWeight: 500}}>
                        Logging in...
                    </Typography>
                </div>
            </div>
        )
    }

    return (
        <div className="login-container">
            {background}
            <div className="login-card">
                <div className="login-header">
                    <Typography
                        variant="h3"
                        className="login-title"
                        style={{fontFamily: 'Cookie, cursive', fontSize: '48px', marginBottom: '4px'}}
                    >
                        Verdantime
                    </Typography>
                    <div className="login-accent-bar"/>
                    <Typography variant="body2" className="secondary-font" style={{marginTop: '16px', marginBottom: 0}}>
                        Welcome back. Please sign in to continue.
                    </Typography>
                </div>

                <div className="login-form">
                    <TextField
                        onChange={e => setUsername(e?.target?.value)}
                        onKeyDown={handleKeyDown}
                        label="Username"
                        variant="outlined"
                        fullWidth
                        className="login-input"
                        autoComplete="username"
                        autoFocus
                        value={providedUsername}
                    />

                    <TextField
                        onChange={e => setPassword(e?.target?.value)}
                        onKeyDown={handleKeyDown}
                        label="Password"
                        variant="outlined"
                        type="password"
                        fullWidth
                        className="login-input"
                        autoComplete="current-password"
                        style={{marginTop: '20px'}}
                        value={providedPassword}
                    />

                    <Button
                        className="login-button primary-font"
                        onClick={() => submitForm(providedUsername, providedPassword)}
                        disabled={isWaitingOnServer || !providedUsername || !providedPassword}
                        variant="contained"
                        fullWidth
                        size="large"
                    >
                        Sign In
                    </Button>
                </div>

                {process.env.NODE_ENV === 'development' && (
                    <>
                        <div style={{display: 'flex', alignItems: 'center', margin: '32px 0'}}>
                            <Divider style={{flex: 1, opacity: 0.3}} />
                            <Typography variant="caption" className="secondary-font" style={{opacity: 0.6, padding: '0 16px', margin: 0}}>
                                OR
                            </Typography>
                            <Divider style={{flex: 1, opacity: 0.3}} />
                        </div>

                        <Button
                            className="demo-button primary-font"
                            onClick={() => submitForm(demoUsername, demoPassword)}
                            disabled={isWaitingOnServer}
                            variant="outlined"
                            fullWidth
                            size="large"
                        >
                            Try Demo Account
                        </Button>
                    </>
                )}
            </div>
        </div>
    );
}

export default LoginPage;
